import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import AssetFieldGrid from './AssetFieldGrid';
import {
  formatCreatedOrModifiedBy,
  formatPicklistValues,
  formatSyncedDate,
  formatSyncedMoney,
  salesforceRecordUrl,
} from './utils';

interface Props {
  asset: OpportunityRoomAsset;
  hideName?: boolean;
  hideSalesforceLinks?: boolean;
  onBack?: () => void;
}

export default function SellableAssetDetailPanel({ asset, hideName, hideSalesforceLinks, onBack }: Props) {
  return (
    <Box>
      {onBack && (
        <Box sx={{ mb: 1, px: 2 }}>
          <Button onClick={onBack} startIcon={<ArrowBackIcon />} variant="text" size="small">
            Back to inclusion asset
          </Button>
        </Box>
      )}

      {!hideName && (
        <Typography variant="h6" sx={{ mb: 2, px: 2 }}>
          {asset.name}
        </Typography>
      )}

      <Box sx={{ mb: 3, px: 2 }}>
        <AssetFieldGrid
          left={[
            {
              label: 'Account',
              value: asset.account__name,
              href: hideSalesforceLinks ? undefined : salesforceRecordUrl('Account', asset.accountid),
            },
            { label: 'Asset Name', value: asset.name },
            { label: 'Asset Currency', value: asset.currencyisocode },
            { label: 'RRP', value: formatSyncedMoney(asset.currencyisocode, asset.rrp__c) },
            { label: 'Inclusion Description', value: asset.inclusion_description__c },
            { label: 'Included Number of Adults', value: asset.included_number_of_adults__c },
            { label: 'PRV', value: asset.prv__c },
            { label: 'Applies', value: asset.applies__c },
            { label: 'Valid For', value: formatPicklistValues(asset.valid_for__c) },
            { label: 'Included Number of Teenagers', value: asset.included_number_of_teenagers__c },
          ]}
          right={[
            { label: 'Current Opp', value: asset.current_opp__c },
            { label: 'Type', value: asset.type__c },
            { label: 'Last Validated On', value: formatSyncedDate(asset.last_validated_on__c) },
            { label: 'Deactivate', checkbox: asset.deactivate__c ?? false },
            { label: 'Description', value: asset.description },
          ]}
        />
      </Box>

      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">System Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AssetFieldGrid
            left={[
              {
                label: 'Created By',
                value: formatCreatedOrModifiedBy(asset.createdby__name, asset.createddate),
              },
            ]}
            right={[
              {
                label: 'Last Modified By',
                value: formatCreatedOrModifiedBy(asset.lastmodifiedby__name, asset.lastmodifieddate),
              },
            ]}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
